import { useMemo, useState } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody, TextField, MenuItem, Stack, Chip, Box, IconButton,
} from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { labelEtapaExpansion, labelTipoOportunidad, TIPOS_OPORTUNIDAD, ETAPAS_EXPANSION } from "./expansionConstants";
import { senalesOportunidad, oportunidadAltoValor } from "./expansionMetrics";

export default function ExpansionList({ store, onEdit, onSeguimiento }) {
  const [filtroEtapa, setFiltroEtapa] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroMonto, setFiltroMonto] = useState("todos");
  const [orden, setOrden] = useState("seguimiento");
  const moneda = store.config.moneda || "";
  const opps = store.oportunidadesExpansion || [];
  const dias = store.config.diasEntreSeguimiento || 7;

  const filas = useMemo(() => {
    let list = [...opps];
    if (filtroEtapa) list = list.filter((o) => o.etapa === filtroEtapa);
    if (filtroTipo) list = list.filter((o) => o.tipo === filtroTipo);
    if (filtroMonto === "con") list = list.filter((o) => Number(o.montoEstimado) > 0);
    if (filtroMonto === "sin") list = list.filter((o) => !(Number(o.montoEstimado) > 0));

    list.sort((a, b) => {
      if (orden === "monto") return (Number(b.montoEstimado) || 0) - (Number(a.montoEstimado) || 0);
      if (orden === "cliente") {
        const na = store.leads.find((l) => l.id === a.leadId)?.nombre || "";
        const nb = store.leads.find((l) => l.id === b.leadId)?.nombre || "";
        return na.localeCompare(nb);
      }
      return (a.proximoSeguimiento || "9").localeCompare(b.proximoSeguimiento || "9");
    });
    return list;
  }, [opps, filtroEtapa, filtroTipo, filtroMonto, orden, store.leads]);

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
        <TextField select size="small" label="Etapa" value={filtroEtapa} onChange={(e) => setFiltroEtapa(e.target.value)} sx={{ minWidth: 120 }}>
          <MenuItem value="">Todas</MenuItem>
          {ETAPAS_EXPANSION.map((e) => <MenuItem key={e} value={e}>{labelEtapaExpansion(e)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Tipo" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} sx={{ minWidth: 120 }}>
          <MenuItem value="">Todos</MenuItem>
          {TIPOS_OPORTUNIDAD.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Monto" value={filtroMonto} onChange={(e) => setFiltroMonto(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="todos">Todos</MenuItem>
          <MenuItem value="con">Con estimación</MenuItem>
          <MenuItem value="sin">Sin estimación</MenuItem>
        </TextField>
        <TextField select size="small" label="Orden" value={orden} onChange={(e) => setOrden(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="seguimiento">Seguimiento</MenuItem>
          <MenuItem value="monto">Monto</MenuItem>
          <MenuItem value="cliente">Cliente</MenuItem>
        </TextField>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cliente</TableCell>
            <TableCell>Oportunidad</TableCell>
            <TableCell>Etapa</TableCell>
            <TableCell align="right">Monto</TableCell>
            <TableCell>Seguimiento</TableCell>
            <TableCell>Señales</TableCell>
            {onSeguimiento && <TableCell width={48} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((o) => {
            const lead = store.leads.find((l) => l.id === o.leadId);
            const senales = senalesOportunidad(o, { diasEstancamiento: dias });
            const alto = oportunidadAltoValor(o, opps);
            return (
              <TableRow
                key={o.id}
                hover
                sx={{ cursor: "pointer", ...(senales.length ? { borderLeft: 3, borderLeftColor: "warning.main" } : {}) }}
                onClick={() => onEdit(o)}
              >
                <TableCell>{lead?.nombre || "—"}</TableCell>
                <TableCell>{o.nombre}{alto ? " ★" : ""}</TableCell>
                <TableCell>{labelEtapaExpansion(o.etapa)}</TableCell>
                <TableCell align="right">
                  {Number(o.montoEstimado) > 0 ? `${moneda} ${Number(o.montoEstimado).toLocaleString()}` : "Pendiente"}
                </TableCell>
                <TableCell>{o.proximoSeguimiento || "—"}</TableCell>
                <TableCell>
                  {senales.slice(0, 2).map((s) => (
                    <Chip key={s} size="small" label={s.replace(/_/g, " ")} sx={{ mr: 0.5, height: 20, fontSize: 10 }} />
                  ))}
                </TableCell>
                {onSeguimiento && (
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="Seguimiento"
                      onClick={(e) => { e.stopPropagation(); onSeguimiento(o); }}
                    >
                      <ForumOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {filas.length === 0 && (
        <Box py={4} textAlign="center" color="text.secondary">No hay oportunidades con estos filtros.</Box>
      )}
    </Box>
  );
}
